import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Tab, Tabs, Table, ListGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPhone, faEnvelope, faCalendar, faCheckCircle, faTimesCircle, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { callQuery, LIDIA_GET_PARTICIPANT_DETAILS, LIDIA_GET_CONVERSATION, LidiaParticipant, LidiaParticipantDetails, LidiaSMSMessage } from '../../amplify/appsync/query';
import { callWrite, LIDIA_UPDATE_PARTICIPANT_STATUS, LIDIA } from '../../amplify/appsync/write';
import { getLocalDateStrFromDate, getLocalTimeStrFromDate, parseAsUTC } from '../../utils/time';
import { dashboardLLMCache } from '../dashboard/llm/cache';

interface LidiaParticipantDetailProps {
    participant: LidiaParticipant;
    onBack: () => void;
}

export const LidiaParticipantDetail = ({ participant, onBack }: LidiaParticipantDetailProps) => {
    const [participantDetails, setParticipantDetails] = useState<LidiaParticipantDetails | null>(null);
    const [conversationHistory, setConversationHistory] = useState<LidiaSMSMessage[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        loadParticipantDetails();
    }, [participant.study_id]);

    const loadParticipantDetails = async () => {
        setIsLoading(true);
        try {
            const details = await callQuery({
                operation: LIDIA_GET_PARTICIPANT_DETAILS,
                study_id: participant.study_id
            });

            setParticipantDetails(details);

            // Load conversation history from cache
            if (details && details.csn) {
                try {
                    const history = await dashboardLLMCache.get(LIDIA, { csn: details.csn, mrn: details.mrn } as any, 'default');
                    setConversationHistory(history || []);
                } catch (error) {
                    console.error('Error loading conversation history:', error);
                    setConversationHistory([]);
                }
            }
        } catch (error) {
            console.error('Error loading participant details:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const updateStatus = async (newStatus: 'ACTIVE' | 'WITHDRAWN' | 'COMPLETED') => {
        if (!window.confirm(`Are you sure you want to change status to ${newStatus}?`)) {
            return;
        }

        setIsUpdating(true);
        try {
            await callWrite({
                operation: LIDIA_UPDATE_PARTICIPANT_STATUS,
                study_id: participant.study_id,
                status: newStatus
            });

            alert(`Status updated to ${newStatus}`);
            onBack();
        } catch (error) {
            console.error('Error updating status:', error);
            alert('Failed to update status');
        } finally {
            setIsUpdating(false);
        }
    };

    const formatDate = (dateStr?: string | null) => {
        if (!dateStr) return 'N/A';
        try {
            const date = parseAsUTC(dateStr);
            return `${getLocalDateStrFromDate(date)} ${getLocalTimeStrFromDate(date)}`;
        } catch {
            return dateStr;
        }
    };

    const getStatusBadge = (status: string) => {
        const variants: Record<string, string> = {
            ACTIVE: 'success',
            WITHDRAWN: 'warning',
            COMPLETED: 'secondary'
        };
        return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
    };

    const getMessageTypeBadge = (type?: string) => {
        const labels: Record<string, string> = {
            WR: 'Waiting Room',
            ROOM: 'In Room',
            PDM: 'Post-Discharge',
            ACTIONABLE_ITEM: 'Actionable Item',
            NEXT_STEP: 'Next Step'
        };
        return <Badge bg="info">{labels[type || ''] || type}</Badge>;
    };

    if (isLoading) {
        return (
            <Container fluid className="py-4">
                <p>Loading participant details...</p>
            </Container>
        );
    }

    if (!participantDetails) {
        return (
            <Container fluid className="py-4">
                <Button variant="outline-secondary" onClick={onBack} className="mb-3">
                    <FontAwesomeIcon icon={faArrowLeft} /> Back
                </Button>
                <p>Participant not found</p>
            </Container>
        );
    }

    return (
        <Container fluid style={{marginTop: '80px'}}>
            <Row className="mb-3">
                <Col>
                    <Button variant="outline-secondary" onClick={onBack}>
                        <FontAwesomeIcon icon={faArrowLeft} /> Back to List
                    </Button>
                </Col>
            </Row>

            {/* Header */}
            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Body>
                            <Row>
                                <Col md={8}>
                                    <h3>{participantDetails.study_id}</h3>
                                    <div className="mb-2">
                                        {getStatusBadge(participantDetails.status)}
                                    </div>
                                    <p className="mb-1"><strong>MRN:</strong> {participantDetails.mrn}</p>
                                    <p className="mb-1"><strong>CSN:</strong> {participantDetails.csn}</p>
                                    {participantDetails.phone_number && (
                                        <p className="mb-1">
                                            <FontAwesomeIcon icon={faPhone} /> {participantDetails.phone_number}
                                        </p>
                                    )}
                                </Col>
                                <Col md={4}>
                                    <p className="mb-1"><strong>Enrolled:</strong> {formatDate(participantDetails.enrollment_date)}</p>
                                    {participantDetails.end_date && (
                                        <p className="mb-1"><strong>End Date:</strong> {formatDate(participantDetails.end_date)}</p>
                                    )}
                                    <p className="mb-1"><strong>Last Activity:</strong> {formatDate(participantDetails.last_activity_date)}</p>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col>
                                    {participantDetails.status === 'ACTIVE' && (
                                        <>
                                            <Button
                                                variant="warning"
                                                size="sm"
                                                onClick={() => updateStatus('WITHDRAWN')}
                                                disabled={isUpdating}
                                                className="me-2"
                                            >
                                                Withdraw
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => updateStatus('COMPLETED')}
                                                disabled={isUpdating}
                                            >
                                                Mark Complete
                                            </Button>
                                        </>
                                    )}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Tabbed Interface */}
            <Row>
                <Col>
                    <Tabs defaultActiveKey="overview" className="mb-3">
                        <Tab eventKey="overview" title="Conversation Overview">
                            <Card>
                                <Card.Body>
                                    {participantDetails.conversations && participantDetails.conversations.length > 0 ? (
                                        <Table responsive>
                                            <thead>
                                                <tr>
                                                    <th>Phone Number</th>
                                                    <th>State</th>
                                                    <th>Messages</th>
                                                    <th>Sent</th>
                                                    <th>Received</th>
                                                    <th>Errors</th>
                                                    <th>Last Activity</th>
                                                    <th>Created</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {participantDetails.conversations.map((conv, idx) => (
                                                    <tr key={idx}>
                                                        <td>{conv.phone_number}</td>
                                                        <td>
                                                            <Badge bg={conv.conversation_state === 'IN_PROGRESS' ? 'primary' : 'secondary'}>
                                                                {conv.conversation_state}
                                                            </Badge>
                                                        </td>
                                                        <td>{conv.message_count}</td>
                                                        <td>{conv.sent_count}</td>
                                                        <td>{conv.received_count}</td>
                                                        <td>
                                                            {conv.error_count > 0 && (
                                                                <Badge bg="danger" title={conv.last_error || 'Error occurred'}>
                                                                    {conv.error_count}
                                                                </Badge>
                                                            )}
                                                        </td>
                                                        <td>{formatDate(conv.last_activity_time)}</td>
                                                        <td>{formatDate(conv.created_at)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    ) : (
                                        <p className="text-muted">No conversations yet</p>
                                    )}
                                </Card.Body>
                            </Card>
                        </Tab>

                        <Tab eventKey="messages" title="Message History">
                            <Card>
                                <Card.Body>
                                    {conversationHistory.length > 0 ? (
                                        <div className="d-flex flex-column gap-3">
                                            {conversationHistory.map((message, idx) => {
                                                const isLidia = message.role === 'LIDIA';
                                                return (
                                                    <div
                                                        key={idx}
                                                        className={`d-flex ${isLidia ? 'justify-content-start' : 'justify-content-end'}`}
                                                    >
                                                        <div
                                                            className={`p-3 rounded ${isLidia ? 'bg-primary text-white' : 'bg-light'}`}
                                                            style={{ maxWidth: '70%' }}
                                                        >
                                                            <div className="mb-2">
                                                                <small>
                                                                    <strong>{message.role}</strong> - {formatDate(message.timestamp)}
                                                                </small>
                                                                {message.message_type && (
                                                                    <span className="ms-2">{getMessageTypeBadge(message.message_type)}</span>
                                                                )}
                                                            </div>
                                                            <p className="mb-0">{message.content}</p>
                                                            {message.metadata?.wait_time !== undefined && (
                                                                <small className="d-block mt-2 opacity-75">
                                                                    Wait time: {message.metadata.wait_time} days
                                                                </small>
                                                            )}
                                                            {message.metadata?.expect_patient_reply === 'yes' && (
                                                                <small className="d-block mt-1 opacity-75">
                                                                    Expecting patient reply
                                                                </small>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="text-muted">No message history available</p>
                                    )}
                                </Card.Body>
                            </Card>
                        </Tab>

                        <Tab eventKey="stats" title="Activity Summary">
                            <Card>
                                <Card.Body>
                                    <Row>
                                        <Col md={6}>
                                            <h5>Overall Statistics</h5>
                                            <ListGroup variant="flush">
                                                <ListGroup.Item>
                                                    <strong>Total Messages:</strong> {participantDetails.message_count || 0}
                                                </ListGroup.Item>
                                                <ListGroup.Item>
                                                    <strong>Messages Sent:</strong> {participantDetails.sent_count || 0}
                                                </ListGroup.Item>
                                                <ListGroup.Item>
                                                    <strong>Messages Received:</strong> {participantDetails.received_count || 0}
                                                </ListGroup.Item>
                                                <ListGroup.Item>
                                                    <strong>Errors:</strong> {participantDetails.error_count || 0}
                                                </ListGroup.Item>
                                            </ListGroup>
                                        </Col>
                                        <Col md={6}>
                                            <h5>Conversation Details</h5>
                                            {participantDetails.conversations && participantDetails.conversations.length > 0 ? (
                                                participantDetails.conversations.map((conv, idx) => (
                                                    <Card className="mb-2" key={idx}>
                                                        <Card.Body>
                                                            <p className="mb-1"><strong>State:</strong> {conv.conversation_state}</p>
                                                            <p className="mb-1"><strong>Messages:</strong> {conv.message_count}</p>
                                                            <p className="mb-0"><strong>Last Activity:</strong> {conv.last_activity}</p>
                                                        </Card.Body>
                                                    </Card>
                                                ))
                                            ) : (
                                                <p className="text-muted">No active conversations</p>
                                            )}
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Tab>
                    </Tabs>
                </Col>
            </Row>
        </Container>
    );
};

export default LidiaParticipantDetail;
